import type {PDFDocumentProxy} from 'pdfjs-dist';
import {IconButton, Tooltip, useTranslationContext} from 'platform/components';
import {Box, getSize, getStepsFromPixels, HStack, Show, Text, VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {usePdfPreviewScroll} from '../../hooks/usePdfPreviewScroll';
import type {PdfPreviewPageInfo} from '../../types/PdfPreviewPageInfo';
import {PdfPreviewPage} from './PdfPreviewPage';

type PdfPreviewDocumentProps = {document: PDFDocumentProxy; pages: PdfPreviewPageInfo[]};

export function PdfPreviewDocument(props: PdfPreviewDocumentProps) {
  const t = useTranslationContext();
  const widestPageWidth = props.pages.reduce((widest, page) => Math.max(widest, page.width), 0);
  const {
    viewport,
    content,
    pages,
    view,
    scale,
    changePage,
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
  } = usePdfPreviewScroll(widestPageWidth);

  return (
    <VStack width="100%" minWidth={0} height="100%" minHeight={0}>
      <Box
        backgroundColor="palettes.neutral.10.100"
        paddingHorizontal={[3, 6]}
        paddingVertical={2}
        flexShrink={0}
        boxShadow="elevation_1"
      >
        <HStack justify="space-between" align="center" spacing={2} wrap>
          <HStack spacing={2} align="center">
            <IconButton
              icon="hardware/keyboard_arrow_left"
              aria-label={t('general.actions.previous')}
              isDisabled={view.pageNumber === 1}
              onClick={() => changePage(view.pageNumber - 1)}
            />
            <Box
              paddingHorizontal={3}
              paddingVertical={1}
              backgroundColor="general.white"
              borderRadius="small"
            >
              <Text size="small" alternative noWrap role="status">
                {`${view.pageNumber} ${t('general.labels.of')} ${props.document.numPages}`}
              </Text>
            </Box>
            <IconButton
              icon="hardware/keyboard_arrow_right"
              aria-label={t('general.actions.next')}
              isDisabled={view.pageNumber === props.document.numPages}
              onClick={() => changePage(view.pageNumber + 1)}
            />
          </HStack>
          <HStack spacing={1} align="center">
            <Tooltip label={t('entity.documentTemplateEditor.labels.zoomOut')}>
              <IconButton
                icon="action/zoom_out"
                aria-label={t('entity.documentTemplateEditor.labels.zoomOut')}
                isDisabled={!canZoomOut}
                onClick={zoomOut}
              />
            </Tooltip>
            <Box minWidth={12}>
              <Text size="small" align="center" noWrap role="status">
                {`${Math.round(scale * 100)}%`}
              </Text>
            </Box>
            <Tooltip label={t('entity.documentTemplateEditor.labels.zoomIn')}>
              <IconButton
                icon="action/zoom_in"
                aria-label={t('entity.documentTemplateEditor.labels.zoomIn')}
                isDisabled={!canZoomIn}
                onClick={zoomIn}
              />
            </Tooltip>
            <Box paddingLeft={2}>
              <Tooltip label={t('entity.documentTemplateEditor.labels.resetZoom')}>
                <IconButton
                  icon="navigation/fullscreen_exit"
                  aria-label={t('entity.documentTemplateEditor.labels.resetZoom')}
                  onClick={resetZoom}
                />
              </Tooltip>
            </Box>
          </HStack>
        </HStack>
      </Box>
      <Box
        ref={viewport}
        role="document"
        position="relative"
        width="100%"
        minWidth={0}
        overflow="auto"
        flex={1}
        minHeight={0}
        tabIndex={0}
      >
        <Box ref={content} padding={[3, 6]}>
          <Box width={`max(100%, ${getSize(getStepsFromPixels(widestPageWidth * scale))})`}>
            <VStack spacing={6} align="center" role="list">
              {props.pages.map((page) => (
                <Box
                  key={page.pageNumber}
                  role="listitem"
                  ref={(element) => {
                    if (isNil(element)) {
                      pages.current.delete(page.pageNumber);
                    } else {
                      pages.current.set(page.pageNumber, element);
                    }
                  }}
                  width={getStepsFromPixels(page.width * scale)}
                  ratio={page.width / page.height}
                  contain="size"
                  backgroundColor="general.white"
                  boxShadow="elevation_3"
                  flexShrink={0}
                  overflow="hidden"
                >
                  <Show
                    when={
                      page.pageNumber >= view.firstRenderedPage &&
                      page.pageNumber <= view.lastRenderedPage
                    }
                  >
                    <PdfPreviewPage document={props.document} pageNumber={page.pageNumber} />
                  </Show>
                </Box>
              ))}
            </VStack>
          </Box>
        </Box>
      </Box>
    </VStack>
  );
}
